import { CharSet } from "refa";
import { Chars } from "./chars";
export function assertNever(value, message) {
    throw new Error(message || value);
}
export function assertSameParent(alternatives) {
    let parent = null;
    for (const a of alternatives) {
        if (parent === null) {
            parent = a.parent;
        }
        else {
            if (a.parent !== parent) {
                throw new Error("Expected all alternatives to have the same parent");
            }
        }
    }
}
export const isReadonlyArray = Array.isArray;
export function asReadonlySet(iter) {
    if (iter instanceof Set) {
        return iter;
    }
    return new Set(iter);
}
export class CharUnion {
    get char() {
        return this._exactChars.union(this._inexactChars);
    }
    get exact() {
        // basic idea here is that the union or an exact superset with an inexact subset will be exact
        return this._exactChars.isSupersetOf(this._inexactChars);
    }
    constructor(empty) {
        this._exactChars = empty;
        this._inexactChars = empty;
    }
    add(char) {
        if (char.exact) {
            this._exactChars = this._exactChars.union(char.char);
        }
        else {
            this._inexactChars = this._inexactChars.union(char.char);
        }
    }
    static fromFlags(flags) {
        return new CharUnion(Chars.empty(flags));
    }
    static fromMaximum(maximum) {
        return new CharUnion(CharSet.empty(maximum));
    }
}
export function unionInexact(left, right) {
    const char = left.char.union(right.char);
    let exact;
    if (left.exact) {
        if (right.exact) {
            exact = true;
        }
        else {
            exact = left.char.isSupersetOf(right.char);
        }
    }
    else {
        if (right.exact) {
            exact = right.char.isSupersetOf(left.char);
        }
        else {
            exact = false;
        }
    }
    return { char, exact };
}
export function intersectInexact(left, right) {
    const char = left.char.intersect(right.char);
    const exact = (left.exact && right.exact) || char.isEmpty;
    return { char, exact };
}
export class SetEquivalence {
    constructor(count) {
        this.count = count;
        this._indexes = [];
        for (let i = 0; i < count; i++) {
            this._indexes.push(i);
        }
    }
    makeEqual(a, b) {
        // This works using the following idea:
        //  1. If the eq set of a and b is the same, then we can stop.
        //  2. If indexes[a] < indexes[b], then we want to make
        //     indexes[b] := indexes[a]. However, this means that we lose the
        //     information about the indexes[b]! So we will store
        //     oldB := indexes[b], then indexes[b] := indexes[a], and then
        //     make oldB == a.
        //  3. If indexes[a] > indexes[b], similar to 2.
        let aValue = this._indexes[a];
        let bValue = this._indexes[b];
        while (aValue !== bValue) {
            if (aValue < bValue) {
                this._indexes[b] = aValue;
                // eslint-disable-next-line no-param-reassign -- x
                b = bValue;
                bValue = this._indexes[b];
            }
            else {
                this._indexes[a] = bValue;
                // eslint-disable-next-line no-param-reassign -- x
                a = aValue;
                aValue = this._indexes[a];
            }
        }
    }
    /**
     * This returns:
     *
     * 1. `eqSet.count`: How many different equivalence classes there are.
     * 2. `eqSet.indexes`: A map (array) from each element (index) to the index
     *    of its equivalence class.
     *
     * All equivalence class indexes `eqSet.indexes[i]` are guaranteed to
     * be <= `eqSet.count`.
     */
    getEquivalenceSets() {
        let counter = 0;
        for (let i = 0; i < this.count; i++) {
            if (i === this._indexes[i]) {
                this._indexes[i] = counter++;
            }
            else {
                this._indexes[i] = this._indexes[this._indexes[i]];
            }
        }
        return {
            count: counter,
            indexes: this._indexes,
        };
    }
}
